import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, Shield, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminUserRoles() {
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    role_id: ''
  });

  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', currentUser?.email],
    queryFn: async () => {
      if (!currentUser?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: currentUser.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: currentUser.email });
      return byEmail[0] || null;
    },
    enabled: !!currentUser?.email
  });

  const { data: userRoles = [], isLoading } = useQuery({
    queryKey: ['userRoles'],
    queryFn: () => base44.entities.UserRole.list('-created_date')
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => base44.entities.Role.filter({ is_active: true })
  });

  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });
  const selectedMemberData = useMemo(
    () => members.find((m) => m.id === formData.user_id),
    [members, formData.user_id]
  );

  const isAdmin = currentUser?.role === 'admin' || permissions?.is_super_admin;

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const member = members.find(m => m.id === data.user_id);
      const role = roles.find(r => r.id === data.role_id);
      
      return await base44.entities.UserRole.create({
        user_id: data.user_id,
        user_email: member.email,
        user_name: member.full_name,
        role_id: data.role_id,
        role_name: role.name,
        assigned_by: currentUser.email,
        assigned_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRoles'] });
      toast.success('Papel atribuído com sucesso!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.UserRole.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRoles'] });
      toast.success('Papel removido com sucesso!');
    }
  });

  const resetForm = () => {
    setFormData({
      user_id: '',
      role_id: ''
    });
    setShowForm(false);
  };

  const handleSubmit = () => {
    if (!formData.user_id || !formData.role_id) {
      toast.error('Selecione um usuário e um papel.');
      return;
    }

    // Verificar se já existe
    const exists = userRoles.some(ur => 
      ur.user_id === formData.user_id && ur.role_id === formData.role_id
    );

    if (exists) {
      toast.error('Este usuário já possui este papel.');
      return;
    }

    createMutation.mutate(formData);
  };

  const filteredUserRoles = userRoles.filter(ur =>
    ur.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    ur.role_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Agrupar por usuário
  const groupedByUser = filteredUserRoles.reduce((acc, ur) => {
    if (!acc[ur.user_id]) {
      acc[ur.user_id] = {
        user_id: ur.user_id,
        user_name: ur.user_name,
        user_email: ur.user_email,
        roles: []
      };
    }
    acc[ur.user_id].roles.push(ur);
    return acc;
  }, {});

  const usersWithRoles = Object.values(groupedByUser);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-lg mx-auto text-center mt-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
          <p className="text-gray-600">Apenas administradores podem gerenciar papéis de usuários.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <PageHeader 
            title="Papéis de Usuários" 
            subtitle={`${usersWithRoles.length} usuários com papéis`}
            showBack={true}
            action={
              <Button onClick={() => setShowForm(true)} size="sm" className="bg-white text-indigo-700 hover:bg-indigo-50">
                <Plus className="w-4 h-4 mr-1" />
                Atribuir Papel
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por usuário ou papel"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {usersWithRoles.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum usuário com papéis"
            description="Atribua papéis para controlar permissões"
            actionLabel="Atribuir Papel"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-3">
            {usersWithRoles.map((user, index) => (
              <motion.div
                key={user.user_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-gray-900">{user.user_name}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{user.user_email}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {user.roles.map(ur => (
                    <div key={ur.id} className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg">
                      <Shield className="w-3 h-3" />
                      <span className="text-sm font-medium">{ur.role_name}</span>
                      <button
                        onClick={() => {
                          if (confirm(`Remover papel "${ur.role_name}"?`)) {
                            deleteMutation.mutate(ur.id);
                          }
                        }}
                        className="text-indigo-400 hover:text-indigo-600 ml-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atribuir Papel</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Usuário *</Label>
              <Select 
                value={formData.user_id} 
                onValueChange={(value) => setFormData({ ...formData, user_id: value })}
              >
                <SelectTrigger>
                  {selectedMemberData ? (
                    <div className="flex items-center gap-2">
                      {selectedMemberData.photo_url ? (
                        <img
                          src={selectedMemberData.photo_url}
                          alt={selectedMemberData.full_name}
                          className="w-6 h-6 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center text-xs font-semibold">
                          {selectedMemberData.full_name?.charAt(0)?.toUpperCase() || 'M'}
                        </div>
                      )}
                      <span className="truncate">{selectedMemberData.full_name}</span>
                    </div>
                  ) : (
                    <SelectValue placeholder="Selecione um usuário" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {members.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-2">
                        {member.photo_url ? (
                          <img
                            src={member.photo_url}
                            alt={member.full_name}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center text-xs font-semibold">
                            {member.full_name?.charAt(0)?.toUpperCase() || 'M'}
                          </div>
                        )}
                        <span>{member.full_name}</span>
                        <span className="text-xs text-gray-500">({member.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Papel *</Label>
              <Select 
                value={formData.role_id} 
                onValueChange={(value) => setFormData({ ...formData, role_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um papel" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                Atribuir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
